import {
  Box3,
  BufferGeometry,
  DoubleSide,
  Material,
  Mesh,
  MeshLambertMaterial,
  Object3D,
  Vector3,
} from "three";
import { buildIfcGeometry, loadIfcMesh } from "../../ifc/ifcMeshCache";

// Share the centred geometry + material across instances of the same IFC url
// (the old committed-mesh path ref-counted resources; the data-driven path must
// too, or N copies of the same object duplicate GPU buffers and tank memory/FPS).
const resCache = new Map<string, { geo: BufferGeometry; mat: Material }>();

/**
 * Build a display object for an IFC url — the client keeps IFC (worker-parsed,
 * cached in ifcMeshCache), so this is what instancesSystem uses in place of
 * poc/editor's synchronous GLB templateById().create().
 *
 * The geometry is recentred so its bottom-centre sits at the local origin: the
 * Instance model treats `position` as the PARENT pivot on the surface, so the
 * object's foot must be at (0,0,0) for `obj.position = pivot` to land it right.
 */
export async function ifcObjectFactory(url: string): Promise<Object3D | null> {
  let res = resCache.get(url);
  if (!res) {
    const data = await loadIfcMesh(url);
    if (!data || data.length === 0) return null;
    const geo = buildIfcGeometry(data);
    geo.computeBoundingBox();
    const box = geo.boundingBox ?? new Box3();
    const c = box.getCenter(new Vector3());
    geo.translate(-c.x, -c.y, -box.min.z); // x/y centred, z-bottom at origin (Z-up)
    const hasColor = geo.hasAttribute("color");
    const mat = new MeshLambertMaterial({
      vertexColors: hasColor,
      color: hasColor ? 0xffffff : 0xb0b0b0,
      side: DoubleSide,
    });
    res = { geo, mat };
    resCache.set(url, res);
  }
  // instances share one geometry+material; each is its own Mesh (own transform).
  return new Mesh(res.geo, res.mat);
}
